/**
 * The page trail map: one entry per admin route, keyed by TanStack route id.
 *
 * Every admin page is a flat child of the `/admin` layout, so the page
 * hierarchy lives here, not in the route tree. Three things read it:
 *   - `usePageTrail` builds each page's breadcrumb by walking `parent` links;
 *   - `AdminSidebar` builds its sections from the entries carrying `nav`, so
 *     the sidebar labels and the breadcrumb labels are one copy;
 *   - `useDocumentTitle` names the browser tab after the leaf entry.
 *
 * `navigation/__tests__/pageTrail.test.ts` fails when an /admin route has no
 * entry here, or an entry names a parent that does not exist.
 */

import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Users,
  FileText,
  Key,
  Database,
  Bot,
  FlaskConical,
  Radio,
  Mail,
  Phone,
  PhoneOutgoing,
  Link2,
  MessageSquare,
  PhoneCall,
  Coins,
  Activity,
  Home,
  CreditCard,
  Settings,
  BookOpen,
  ShieldAlert,
  Building2,
} from 'lucide-react';

/** The admin index route's id (the tenant's landing page at /admin). */
export const ADMIN_INDEX_ROUTE_ID = '/admin/';

export type NavSectionTitle =
  | 'Overview'
  | 'Channels'
  | 'Management'
  | 'Monitoring'
  | 'Development'
  | 'Livepoint';

export interface NavSection {
  title: NavSectionTitle;
  /**
   * Leave the whole section out (rather than show it disabled) for a user who
   * cannot use it. For Livepoint's own tools: a tenant user must not even see
   * that usage is tracked.
   */
  hiddenWithoutAccess?: boolean;
}

/** Sidebar sections, in display order. Items keep the map's order within each. */
export const NAV_SECTIONS: readonly NavSection[] = [
  { title: 'Overview' },
  { title: 'Channels' },
  { title: 'Management' },
  { title: 'Monitoring' },
  { title: 'Development' },
  { title: 'Livepoint', hiddenWithoutAccess: true },
];

export interface PageTrailNav {
  section: NavSectionTitle;
  icon: LucideIcon;
  badge?: string;
}

/** What a page's trail entry can vary by from its search params. */
export interface PageTrailVariant {
  label?: string;
  parent?: string;
}

export interface PageTrailEntry {
  /** The crumb label, the sidebar label and the browser tab's page name. */
  label: string;
  /** The route id of the page this one sits under; omitted = under the tenant. */
  parent?: string;
  /** Present when the page is a sidebar item. */
  nav?: PageTrailNav;
  /**
   * The header icon of a page that is not a sidebar item. Sidebar pages use
   * `nav.icon`; a page with neither takes its parent's icon.
   */
  icon?: LucideIcon;
  /** A label or parent that depends on the URL's search params. */
  fromSearch?: (search: Record<string, unknown>) => PageTrailVariant;
}

export const PAGE_TRAIL: Readonly<Record<string, PageTrailEntry>> = {
  [ADMIN_INDEX_ROUTE_ID]: { label: 'Home', icon: Home },

  // Overview
  '/admin/dashboard': { label: 'Dashboard', nav: { section: 'Overview', icon: LayoutDashboard } },

  // Channels
  '/admin/agents': { label: 'Agents', nav: { section: 'Channels', icon: Bot } },
  '/admin/voice-configs': { label: 'Voice', nav: { section: 'Channels', icon: Phone } },
  '/admin/outbound-calls': { label: 'Outbound calls', nav: { section: 'Channels', icon: PhoneOutgoing } },
  '/admin/conversations': { label: 'Conversations', nav: { section: 'Channels', icon: MessageSquare } },
  '/admin/email-templates': { label: 'Email', nav: { section: 'Channels', icon: Mail } },

  // Management
  '/admin/users': { label: 'Users', nav: { section: 'Management', icon: Users } },
  '/admin/api-keys': { label: 'Developer API', nav: { section: 'Management', icon: Key } },
  '/admin/integrations': { label: 'Integrations', nav: { section: 'Management', icon: Link2 } },
  '/admin/phone-numbers': { label: 'Phone numbers', nav: { section: 'Management', icon: PhoneCall } },

  // Monitoring
  '/admin/audio-monitor': { label: 'Live calls', nav: { section: 'Monitoring', icon: Radio, badge: 'Live' } },
  '/admin/audit-logs': { label: 'Audit logs', nav: { section: 'Monitoring', icon: FileText } },
  '/admin/system': { label: 'System', nav: { section: 'Monitoring', icon: Database } },

  // Development
  '/admin/evaluation': { label: 'Evaluation', nav: { section: 'Development', icon: FlaskConical } },

  // Livepoint
  '/admin/platform/tenants': { label: 'Tenants', nav: { section: 'Livepoint', icon: Building2 } },
  '/admin/platform/usage': { label: 'Tenant usage', nav: { section: 'Livepoint', icon: Coins } },
  '/admin/platform/call-quality': { label: 'Call quality', nav: { section: 'Livepoint', icon: Activity } },
  // A tenant's account, opened from Tenants. The page overrides this label with the tenant's display name.
  '/admin/platform/tenants/$tenantId': { label: 'Tenant', parent: '/admin/platform/tenants' },
  // Every tenant's numbers, opened from Tenants.
  '/admin/platform/numbers': { label: 'All numbers', parent: '/admin/platform/tenants' },
  // A tenant's usage, opened from Tenant usage. The page overrides this label
  // with the tenant's display name, and the crumb back keeps the period.
  '/admin/platform/usage/$tenantId': { label: 'Tenant', parent: '/admin/platform/usage' },

  // Pages reached from the account menu, the landing page or a banner
  // Usage and Billing use the account menu's icons.
  '/admin/usage': { label: 'Usage', icon: Activity },
  '/admin/billing': { label: 'Billing', icon: CreditCard },
  '/admin/usage/topup': {
    label: 'Buy credit',
    parent: '/admin/usage',
    // Opened from the billing page, the way back up is billing.
    fromSearch: (search) => (search.from === 'billing' ? { parent: '/admin/billing' } : {}),
  },
  '/admin/settings': { label: 'Settings', icon: Settings },
  // The page overrides this label with the walkthrough's title.
  '/admin/walkthroughs/$slug': { label: 'Walkthrough', icon: BookOpen },
  '/admin/unauthorized': {
    label: 'Authentication required',
    icon: ShieldAlert,
    // `required` (route guard) or `forbidden` (403 interceptor) selects the 403 variant.
    fromSearch: (search) => (search.required || search.forbidden ? { label: 'Access denied' } : {}),
  },
};

/**
 * A page's header icon: its sidebar icon, else its own, else its parent's
 * (so Buy credit shows Usage's icon, a tenant's account shows Tenants'
 * and a tenant's usage shows Tenant usage's).
 */
export function pageTrailIcon(routeId: string, search: Record<string, unknown> = {}): LucideIcon | undefined {
  let id: string | undefined = routeId;
  // Same cap as the trail walk: a bad edit can never hang a render.
  for (let hops = 0; id && hops < 10; hops++) {
    const entry: PageTrailEntry | undefined = PAGE_TRAIL[id];
    if (!entry) return undefined;
    const icon = entry.nav?.icon ?? entry.icon;
    if (icon) return icon;
    id = entry.fromSearch?.(search).parent ?? entry.parent;
  }
  return undefined;
}

/** The path a trail entry's crumb links to: its route id, minus the index slash. */
export function trailPath(routeId: string): string {
  return routeId === ADMIN_INDEX_ROUTE_ID ? '/admin' : routeId;
}
